import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { safetyContent } from '../data/content';
import { 
  Shield, Camera, KeyRound, Phone, Flame, Users 
} from 'lucide-react';

const iconMap = {
  Shield,
  Camera,
  KeyRound,
  Phone,
  Flame,
  Users
};

const SafetyPage = () => {
  return (
    <div data-testid="safety-page" className="bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-orange-50 to-white relative overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="section-container relative z-10">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-sm font-semibold mb-4">
              Security First
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 font-['Outfit'] mb-6">
              {safetyContent.title.replace('Priority', '')} <span className="text-coral">Priority</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl">
              {safetyContent.intro}
            </p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="section-spacing bg-white">
          <div className="section-container">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {safetyContent.features.map((feature, idx) => {
                const Icon = iconMap[feature.icon] || Shield;
                return (
                  <div
                    key={idx}
                    data-testid={`safety-feature-${idx}`}
                    className="bg-white rounded-2xl p-8 border border-orange-100 hover:border-coral/30 hover:shadow-lg transition-all"
                  >
                    <div className="w-16 h-16 bg-coral/10 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-coral" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 font-['Outfit'] mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Security Image Section */}
        <section className="section-spacing bg-orange-50 relative overflow-hidden">
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-coral/5 rounded-full blur-3xl" />
          <div className="section-container relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Outfit'] mb-6">
                  Peace of Mind, <span className="text-coral">Always</span>
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                  Our comprehensive security infrastructure ensures you can focus on what matters most – your studies. From controlled access points to round-the-clock security personnel, we've left nothing to chance.
                </p>
                <ul className="space-y-4">
                  {[
                    'Biometric access control at all entrances',
                    'CCTV monitoring of common areas',
                    'Regular security patrols',
                    'Emergency response protocols',
                    'Direct line to security office'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl border border-orange-100">
                <img
                  src="https://images.unsplash.com/photo-1652148555073-4b1d2ecd664c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwZnJpZW5kbHklMjBwcm9mZXNzaW9uYWwlMjB1bmlmb3JtfGVufDB8fHx8MTc3MDk5MDYzOXww&ixlib=rb-4.1.0&q=85&w=600"
                  alt="24/7 Security personnel at Apollo House"
                  className="w-full h-96 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default SafetyPage;
